/**
 * The two raster ops on an existing plan (TD §5, §7.2, §7.3), both immer draft mutators:
 * rearrangeHabitats re-slices the unlocked zones around the current roads and water;
 * carveAreas trims unlocked zones off roads and re-traces them with shared walls.
 * Locked zones are never moved, trimmed or re-traced (FD §4).
 */
import { speciesNeeds } from '../../shared/species-needs';
import type { AnimalArea, AreaId, PlanDoc, Pt, RoadId, Species } from '../../shared/types';
import { centroid, distPoly, pip, roadPoly } from '../geom';
import { isAnimalArea } from '../model';
import { pruneOrphans } from '../ops';
import { forCellsInPoly, forCellsNearLine, keepLargestPieces, nearestOnRoutes, rng } from '../raster';
import { addPool, areaRing, classify, deletePoolsOf, fillZones, idMaker, ROAD_MARGIN_CELLS, traceInto, walkingRoutes, type ZoneItem } from './fill';
import { groupPicks } from './themes';

/** Space assumed for a zone whose species isn't in the list (m²). */
export const UNKNOWN_SPECIES_AREA = 400;

/** Stand-in species for a zone whose species id is unknown. */
function unknownSpecies(a: AnimalArea): Species {
  return {
    id: a.speciesId, name: 'Unknown animal', kind: a.kind, biome: 'temperate', regions: [],
    area: { min: UNKNOWN_SPECIES_AREA, perExtra: 0 }, water: { min: 0, perExtra: 0 },
    groupSize: { min: 1, max: 1, typical: 1 }, estimate: true,
  };
}

/** Unlocked animal zones that have a ring. */
function unlockedZones(doc: PlanDoc): [AreaId, AnimalArea][] {
  return Object.entries(doc.areas).filter((e): e is [AreaId, AnimalArea] => isAnimalArea(e[1]) && !e[1].locked && e[1].verts.length >= 3);
}

/**
 * Re-slices every unlocked habitat, aviary and aquarium around the current roads and water (TD §7.2), keeping
 * their ids, species and counts. Regions are grouped by biome and keep the walking order they have now; the
 * aquatic group goes by the water. Locked zones are reserved and keep their corners; a re-sliced zone along a
 * locked one gets its own corners on that wall. Zones that no longer fit keep their old shape and are returned
 * in `unplaced`. Pools of re-sliced zones are rebuilt. With `priority` (the zone "Make room" is for), that zone gets
 * its frontage and its needed area first, and the others share what is left. Deterministic for a given doc and seed.
 */
export function rearrangeHabitats(draft: PlanDoc, species: Species[], opts: { seed: number; priority?: AreaId }): { unplaced: AreaId[] } {
  const zones = unlockedZones(draft);
  if (!zones.length) return { unplaced: [] };
  const byId = new Map(species.map(s => [s.id, s])), routes = walkingRoutes(draft);
  // sort by where each zone sits along the walk now, so biome groups keep their current order
  const pos = new Map(zones.map(([id]) => {
    const ring = areaRing(draft, id), c = ring.length ? centroid(ring) : [0, 0] as Pt, q = nearestOnRoutes(routes, c[0], c[1]);
    return [id, q.ri < 0 ? 0 : (routes[q.ri]?.off ?? 0) + q.s] as const;
  }));
  zones.sort((a, b) => (pos.get(a[0]) ?? 0) - (pos.get(b[0]) ?? 0));
  const sps = zones.map(([, a]) => byId.get(a.speciesId) ?? unknownSpecies(a));
  const needs = zones.map(([, a], i) => speciesNeeds(sps[i] as Species, a.count, draft.settings.extraSpacePct));
  const grouping = groupPicks(sps, needs.map(n => n.water), [], []);
  const items: ZoneItem[] = zones.map(([id, a], i) => ({
    areaId: id, speciesId: a.speciesId, count: a.count, kind: a.kind, group: grouping.groupOf[i] as number, need: needs[i] as { area: number; water: number },
    ...(id === opts.priority ? { priority: true } : {}),
  }));
  const placed = fillZones(draft, items, grouping.groups, grouping.waterGroup, rng(opts.seed), idMaker());
  pruneOrphans(draft);
  return { unplaced: zones.flatMap(([id], i) => (placed[i] ? [] : [id])) };
}

/**
 * Trims unlocked zones off the given roads (TD §7.3): the zones are rasterized, the cells under each road (plus a
 * small margin) are cleared, a zone cut in two keeps its bigger piece, and all unlocked zones are re-traced
 * together so their shared walls stay structural. A zone left with almost nothing is deleted. Pools of trimmed
 * zones that ended up under the road or outside their zone are rebuilt. Returns the trimmed (or deleted) zones.
 */
export function carveAreas(draft: PlanDoc, roadIds: RoadId[], species: Species[]): { trimmed: AreaId[] } {
  const zones = unlockedZones(draft), roads = roadIds.flatMap(id => { const r = draft.roads[id]; return r ? [roadPoly(draft, r)] : []; });
  if (!zones.length || !roads.length) return { trimmed: [] };
  const cl = classify(draft, new Set(zones.map(([id]) => id))), { g } = cl, lab = new Int32Array(g.n).fill(-1);
  zones.forEach(([id], li) => forCellsInPoly(g, areaRing(draft, id), k => { if ((lab[k] as number) < 0 && !cl.kept[k]) lab[k] = li; }));
  const touched = new Set<number>(), corridor = (rp: { widths: number[] }) => (s: number) => Math.max(rp.widths[s] ?? 0, rp.widths[s + 1] ?? 0) / 2 + g.c * ROAD_MARGIN_CELLS;
  for (const rp of roads) {
    forCellsNearLine(g, rp.pts, corridor(rp), k => { const l = lab[k] as number; if (l >= 0) { touched.add(l); lab[k] = -1; } });
  }
  if (!touched.size) return { trimmed: [] };
  keepLargestPieces(g, lab, 4);
  const ids = idMaker(), rings = traceInto(draft, g, lab, cl.hard, ids), byId = new Map(species.map(s => [s.id, s])), trimmed: AreaId[] = [];
  const underRoad = (ring: Pt[]) => ring.some(p => roads.some(rp => distPoly(rp.pts, p[0], p[1], false) <= Math.max(...rp.widths) / 2 + 1));
  zones.forEach(([id, a], li) => {
    const ring = rings.get(li);
    if (ring) a.verts = ring;
    else if (touched.has(li)) { deletePoolsOf(draft, id); delete draft.areas[id]; }
    if (!touched.has(li)) return;
    trimmed.push(id);
    if (!ring) return;
    const zoneRing = areaRing(draft, id);
    let lost = false;
    for (const [pid, p] of Object.entries(draft.areas)) {
      if (p.kind !== 'pool' || p.parent !== id) continue;
      const pr = areaRing(draft, pid), c = pr.length ? centroid(pr) : null;
      if (!c || !pip(zoneRing, c[0], c[1]) || underRoad(pr)) { delete draft.areas[pid]; lost = true; }
    }
    const sp = byId.get(a.speciesId);
    if (lost && sp) addPool(draft, cl, lab, li, speciesNeeds(sp, a.count, draft.settings.extraSpacePct).water, id, ids);
  });
  pruneOrphans(draft);
  return { trimmed };
}
